import { SectionsHelper } from 'classes/helpers/sections.helper';
import { SectionName } from 'enums/route.enums';
import { ISection } from 'interfaces/i-section';
import { getUserFeatureFlags } from 'interfaces/i-user-feature-flags';
import { useCallback, useMemo } from 'react';
import { useAuthStore } from 'stores/use-auth-store';

const isVisible = (sections: ISection[], name: SectionName) =>
  sections.some((s) => !s.invisible && s.value === name);

/** Permission-aware nav catalog (replaces SectionsContext user/admin sections). */
export const useNavSections = () => {
  const current = useAuthStore((s) => s.current);
  const userRoleFeatureFlags = useAuthStore((s) => s.userRoleFeatureFlags);
  const isInGame = useAuthStore((s) => s.isInGame);

  const userSections = useMemo(
    () =>
      SectionsHelper.getUserSections({
        flags: userRoleFeatureFlags,
        permissions: current,
        isInGame,
      }),
    [current, userRoleFeatureFlags, isInGame]
  );

  const adminSections = useMemo(
    () => SectionsHelper.getAdminSections(userRoleFeatureFlags),
    [userRoleFeatureFlags]
  );

  const isSectionAvailable = useCallback(
    (name: SectionName) => {
      if (isVisible(userSections, name)) {
        return true;
      }

      if (isVisible(adminSections, name)) {
        return true;
      }

      return false;
    },
    [userSections, adminSections]
  );

  // todo: this sucks
  // We need "unrestricted" copies of the sections so we can differentiate between sections that are
  // restricted due to a live game and sections that are never available to the user.
  // See SectionGate for usage
  const unrestrictedFlags = useMemo(
    () => getUserFeatureFlags(current.role, false),
    [current.role]
  );

  const unrestrictedUserSections = useMemo(
    () =>
      SectionsHelper.getUserSections({
        flags: unrestrictedFlags,
        permissions: current,
        isInGame: false,
      }),
    [current, unrestrictedFlags]
  );

  const unrestrictedAdminSections = useMemo(
    () => SectionsHelper.getAdminSections(unrestrictedFlags),
    [unrestrictedFlags]
  );

  const isBlockedByGame = useCallback(
    (name: SectionName) => {
      if (isSectionAvailable(name)) {
        return false;
      }

      if (!isInGame) {
        return false;
      }

      if (isVisible(unrestrictedUserSections, name)) {
        return false;
      }

      if (isVisible(unrestrictedAdminSections, name)) {
        return false;
      }

      return true;
    },
    [
      isInGame,
      isSectionAvailable,
      unrestrictedUserSections,
      unrestrictedAdminSections,
    ]
  );

  return {
    userSections,
    adminSections,
    isSectionAvailable,
    isBlockedByGame,
  };
};
